/**
 * Денежный словарь системы: валюты, способы оплаты, роли принимающего и минорные единицы.
 *
 * Один источник на все слои. До этого те же перечисления были выписаны пятью независимыми
 * списками — в домене, в схемах маршрутов, в схемах веб-клиента и дважды в компонентах, — и
 * компилятор не видел расхождения между ними: добавленная валюта или отобранный у инструктора
 * способ оплаты означали пять ручных правок, четыре из которых легко забыть.
 *
 * Здесь только словарь: zod-схемы, типы и константы, никакой арифметики. Считает деньги
 * `packages/domain/src/ledger.ts`, решает «можно ли» — он же.
 */
import { z } from "zod";
/** Деньги локации — валюта, в которой выражен долг сделки. */
export const SettlementCurrencySchema = z.enum(["IDR", "THB"]);
/** Что клиент действительно передал: «расчётная» на карточке счёта (§3.3). */
export const OriginalCurrencySchema = z.enum(["USDT", "USD", "RUB", "IDR", "THB"]);
/**
 * Способ оплаты. Рабочих четыре — наличные, перевод, крипта, сертификат.
 *
 * «Карта», «Перевод внутри страны» и «Перевод в рублях» — один и тот же канал, разложенный
 * когда-то по признакам, которые ничего не решают: банк на той стороне не меняет ни правил
 * приёма, ни арифметики. Они остаются в перечислении, потому что журнал и карточки счетов
 * amo полны старых чеков с этими подписями, и строка, которую нельзя прочитать, — это
 * потерянные деньги. Новый чек ими не оформляется: `ACTIVE_PAYMENT_METHODS` — то, что
 * предлагается человеку, `LEGACY_PAYMENT_METHODS` — то, что только читается.
 */
export const PaymentMethodSchema = z.enum([
    "cash",
    "transfer",
    "crypto",
    "certificate",
    "local_transfer",
    "rub_transfer",
    "card",
]);
/** Способы, которыми оформляется новый чек. Порядок — тот, в котором их рисуют. */
export const ACTIVE_PAYMENT_METHODS = Object.freeze([
    "cash",
    "transfer",
    "crypto",
    "certificate",
]);
/** Способы, которые остались только в истории: читаются, но не предлагаются. */
export const LEGACY_PAYMENT_METHODS = Object.freeze([
    "local_transfer",
    "rub_transfer",
    "card",
]);
export function isLegacyPaymentMethod(method) {
    return LEGACY_PAYMENT_METHODS.includes(method);
}
/**
 * Какие валюты ходят каким способом. Пары, а не два независимых списка: «USDT наличными» и
 * «рупии криптой» — это не редкий случай, а опечатка, и ловить её должен словарь, а не
 * бухгалтер месяцем позже.
 *
 * Сертификат — бланк, выписанный в деньгах площадки, поэтому у него только IDR и THB;
 * какая из двух, решает валюта расчёта сделки (см. `assessPaymentAdmissibility`).
 */
export const METHOD_CURRENCIES = Object.freeze({
    cash: Object.freeze(["USD", "RUB", "IDR", "THB"]),
    transfer: Object.freeze(["USD", "RUB", "IDR", "THB"]),
    crypto: Object.freeze(["USDT"]),
    certificate: Object.freeze(["IDR", "THB"]),
    // Историческое: пары старых чеков, чтобы прочитанная строка не выглядела невозможной.
    local_transfer: Object.freeze(["IDR", "THB"]),
    rub_transfer: Object.freeze(["RUB"]),
    card: Object.freeze(["USD", "IDR", "THB"]),
});
/** Способы, которыми можно принять эту валюту, — тот же словарь с другой стороны. */
export function methodsForCurrency(currency) {
    return ACTIVE_PAYMENT_METHODS.filter((method) => METHOD_CURRENCIES[method].includes(currency));
}
/** Валюты, которые можно принять этим способом, — только рабочие способы что-то предлагают. */
export function currenciesForMethod(method) {
    return METHOD_CURRENCIES[method];
}
export function isCurrencyAllowedForMethod(method, currency) {
    return METHOD_CURRENCIES[method].includes(currency);
}
/** «Тип операции» 1134453: возврат — отдельный счёт с положительной суммой (§3.3). */
export const OperationTypeSchema = z.enum(["deposit", "refund"]);
/** «Принял» 1134473 — роль, а не человек: человек живёт только в нашем журнале. */
export const AcceptorRoleSchema = z.enum(["instructor", "manager"]);
export const LedgerEntryStatusSchema = z.enum(["pending", "confirmed", "rejected"]);
export const PaymentStatusSchema = z.enum([
    "not_paid",
    "advance",
    "partial",
    "paid",
    "overpaid",
    /** У сделки нет локации: валюта расчёта неизвестна, поэтому локальные деньги ещё не число. */
    "unknown",
]);
/**
 * Множитель минорных единиц. USDT, USD и RUB считаются в центах и копейках — все три валюты
 * котируются с дробной частью; IDR и THB целые, дробной части у них здесь нет, и amo хранит
 * эти поля целыми числами.
 */
export const MINOR_UNITS = Object.freeze({
    USDT: 100,
    USD: 100,
    RUB: 100,
    IDR: 1,
    THB: 1,
});
/**
 * Способы, доступные инструктору (§7.4). Сертификат остаётся за менеджером: бланк принимают
 * в офисе, у инструктора его нечем ни проверить, ни погасить.
 */
export const INSTRUCTOR_PAYMENT_METHODS = Object.freeze([
    "cash",
    "transfer",
    "crypto",
]);
export function isInstructorPaymentMethod(method) {
    return INSTRUCTOR_PAYMENT_METHODS.includes(method);
}
